import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Claim, EvidenceVersion } from "@edufadder/shared-types";
import { Chip, type ChipTone } from "@edufadder/ui";
import { CuratorReadingPanel } from "../components/CuratorReadingPanel.js";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

const strengthTone: Record<string, ChipTone> = {
  supported: "green",
  mixed_or_contested: "amber",
  not_adequately_supported: "red",
  insufficient_evidence: "neutral",
};

const strengthLabel: Record<string, string> = {
  supported: "Well supported by research",
  mixed_or_contested: "Research is mixed",
  not_adequately_supported: "Not well supported by research so far",
  insufficient_evidence: "Too little research to say yet",
};

const verificationTone: Record<string, ChipTone> = { verified: "green", needs_review: "amber", unverified: "neutral" };

export function ClaimReportPage(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const { me } = useAuth();
  const [claim, setClaim] = useState<Claim | null>(null);
  const [publishedVersion, setPublishedVersion] = useState<EvidenceVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState("");

  useEffect(() => {
    if (!claimId) return;
    Promise.all([api.getClaim(claimId), api.listVersions(claimId)])
      .then(([c, versions]) => {
        setClaim(c);
        // At most one version per claim can be "published" at a time — see
        // services/api/app/services/evidence.py `publish_release`, which demotes
        // the previous published version to "superseded" in the same transaction
        // it promotes the new one, so this is always the single currently-live,
        // release-accurate version for this claim (if any exists at all).
        setPublishedVersion(versions.find((v) => v.status === "published") ?? null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load this claim."));
  }, [claimId]);

  async function sendFeedback(kind: string): Promise<void> {
    if (!claimId) return;
    await api.createFeedback({ kind, claim_id: claimId, note: feedbackNote });
    setFeedbackSent(true);
  }

  if (error) {
    return (
      <div style={s.container}>
        <p style={s.errorText}>{error}</p>
      </div>
    );
  }

  if (!claim) {
    return (
      <div style={s.container}>
        <p style={s.mutedText}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>{claim.label}</h1>
      <p style={s.mutedText}>Topic: {claim.topic}</p>
      {claim.aliases.length > 0 && <p style={s.mutedText}>Also known as: {claim.aliases.join(", ")}</p>}

      {!publishedVersion && (
        <div style={s.card}>
          <Chip tone="neutral">insufficient_evidence</Chip>
          <p style={{ marginTop: 8 }}>
            EduFadder doesn't have published, curated evidence for this claim yet. This is deliberate — the system is
            biased toward saying nothing rather than guessing.
          </p>
        </div>
      )}

      {publishedVersion && (
        <div style={s.card}>
          <Chip tone={strengthTone[publishedVersion.evidence_strength] ?? "neutral"}>
            {strengthLabel[publishedVersion.evidence_strength] ?? publishedVersion.evidence_strength}
          </Chip>
          <p style={{ marginTop: 12 }}>{publishedVersion.summary}</p>
          {publishedVersion.qualifications && (
            <>
              <h2 style={s.heading2}>Qualifications</h2>
              <p>{publishedVersion.qualifications}</p>
            </>
          )}
          {publishedVersion.alternative_framing && (
            <>
              <h2 style={s.heading2}>A better way to frame this</h2>
              <p>{publishedVersion.alternative_framing}</p>
            </>
          )}

          <h2 style={s.heading2}>Sources</h2>
          {publishedVersion.sources.length === 0 && <p style={s.mutedText}>No sources recorded.</p>}
          <ul style={{ listStyle: "none", padding: 0 }}>
            {publishedVersion.sources.map((src) => (
              <li key={src.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #ddd7c7" }}>
                <div>
                  {src.citation}
                  {src.identifier && (
                    <>
                      {" "}
                      —{" "}
                      {/^https?:\/\//.test(src.identifier) ? (
                        <a href={src.identifier} target="_blank" rel="noopener noreferrer">
                          {src.identifier}
                        </a>
                      ) : (
                        <span>{src.identifier}</span>
                      )}
                    </>
                  )}
                </div>
                <div style={{ marginTop: 4, display: "flex", gap: 8, alignItems: "center" }}>
                  {src.role && <span style={s.mutedText}>{src.role}</span>}
                  <Chip tone={verificationTone[src.verification_status] ?? "neutral"}>{src.verification_status}</Chip>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {claimId && me?.permissions.includes("claims:read") && (
        <CuratorReadingPanel
          claimId={claimId}
          canEditQuery={me.permissions.includes("claims:write")}
          canModerate={me.permissions.includes("claims:approve")}
        />
      )}

      {me && (
        <div style={s.card}>
          <h2 style={s.heading2}>Tell us what you think</h2>
          {feedbackSent ? (
            <p style={s.mutedText}>Thanks — this has been sent to our curation team.</p>
          ) : (
            <>
              <textarea
                style={s.textarea}
                placeholder="Optional note…"
                value={feedbackNote}
                onChange={(e) => setFeedbackNote(e.target.value)}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" style={s.buttonSecondary} onClick={() => void sendFeedback("evidence_card_helpful")}>
                  This was helpful
                </button>
                <button type="button" style={s.buttonSecondary} onClick={() => void sendFeedback("evidence_card_unhelpful")}>
                  This wasn't helpful
                </button>
                <button type="button" style={s.buttonSecondary} onClick={() => void sendFeedback("not_about_this_idea")}>
                  This isn't about this idea
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
